import { Box, LinearProgress, Skeleton, Typography } from '@mui/material';
import { FC, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { PolicyDocumentType, DocumentsClient } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { setPolicyDocuments } from '@acorn-portal/shared/src/store/policyDocuments.slice';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import TimerIcon from '@acorn-portal/shared/src/assets/shared/icons/base/timer.svg?react';
import QuestionIcon from '@acorn-portal/shared/src/assets/shared/icons/base/circled-question-mark.svg?react';
import OpenDocumentButton from './open-document-button';
import { getDocument } from '@acorn-portal/shared/src/utils';

const PolicyDocuments: FC = () => {
	const { policyToken } = useParams();
	const dispatch = useAppDispatch();
	const policyDocuments = useAppSelector((x) => x.policyDocuments);
	const fullPolicy = useAppSelector((x) => x.fullPolicy);
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [isDocumentExist, setIsDocumentExist] = useState(false);
	const [downloadLoading, setDownloading] = useState(false);
	const isLoadingDocs = useRef(false);

	useEffect(() => {
		const getPolicyDocuments = async () => {
			if (isLoadingDocs.current) {
				return;
			}

			// If documents are already loaded, don't fetch again
			if (Object.keys(policyDocuments).length > 0) {
				return;
			}

			isLoadingDocs.current = true;
			setLoading(true);

			if (policyToken) {
				const client = new DocumentsClient(undefined, API);
				const documents = await client.getLatestDocuments(policyToken);
				if (documents.length === 0) {
					setIsDocumentExist(true);
				} else {
					dispatch(setPolicyDocuments(documents));
				}
			}
			setLoading(false);
			isLoadingDocs.current = false;
		};
		getPolicyDocuments().catch(() => {
			setError(true);
			setLoading(false);
			isLoadingDocs.current = false;
		});
	}, [policyToken, dispatch, policyDocuments]);

	if (loading) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Skeleton height={80} />
				<Skeleton height={80} />
				<Skeleton height={80} />
				<Skeleton height={80} />
				<Skeleton height={80} />
			</Box>
		);
	}

	if (error) {
		return (
			<Box pt={0} pb={3}>
				<Box
					sx={{
						'& svg': { maxHeight: '50px' },
						display: 'flex',
						justifyContent: 'center',
					}}
				>
					<QuestionIcon />
				</Box>
				<Typography textAlign='center' sx={{ marginTop: 2 }}>
					Unable to retrieve policy documents, please contact us on web chat for your documents.
				</Typography>
			</Box>
		);
	}

	if (isDocumentExist) {
		return (
			<Box pt={0} pb={3}>
				<Box
					sx={{
						'& svg': { maxHeight: '50px' },
						display: 'flex',
						justifyContent: 'center',
					}}
				>
					<TimerIcon />
				</Box>
				<Typography textAlign='center' sx={{ marginTop: 2 }}>
					Your documents will be available shortly. Please check again in a few minutes.
				</Typography>
			</Box>
		);
	}

	const policyBookletUrl = getDocument(
		fullPolicy.product,
		fullPolicy.isShortTermPolicy,
		PolicyDocumentType.POLICY_WORDING,
	);

	const bookletExists = Object.values(policyDocuments).find(
		(doc) => doc.documentType === PolicyDocumentType.POLICY_WORDING,
	);

	const documents =
		policyBookletUrl && !bookletExists
			? [
					{
						documentType: PolicyDocumentType.POLICY_WORDING,
						url: policyBookletUrl,
						description: 'Policy Booklet',
					},
					...Object.values(policyDocuments),
				]
			: Object.values(policyDocuments);

	return (
		!error &&
		!loading && (
			<>
				{downloadLoading ? (
					<Box>
						<LinearProgress />
					</Box>
				) : null}
				{documents.map((item) => (
					<OpenDocumentButton key={item.documentType} item={item} setDownloading={setDownloading} />
				))}
			</>
		)
	);
};

export default PolicyDocuments;
